import { lazy } from 'react';

// project imports
import MainLayout from 'layout/MainLayout';
import Loadable from 'ui-component/Loadable';
import AuthGuard from 'utils/route-guard/AuthGuard';
import PriorityGuard from 'utils/route-guard/PriorityGuard';
import { RouteObject, Navigate } from 'react-router';
import FilesListLayout from 'views/files/FilesListLayout';
import CreateEditTemplate from 'views/messages-templates/createEdit';
import ErrorBoundary from 'views/pages/maintenance/ErrorBoundary';

// sample page routing
// const SamplePage = Loadable(lazy(() => import('views/sample-page')));
const ComingSoon = Loadable(lazy(() => import('views/pages/maintenance/ComingSoon/ComingSoon2')));

//* External App
const ExternalAppPage = Loadable(lazy(() => import('views/external-app')));

//* Statistics
const Statistics = Loadable(lazy(() => import('views/statistics')));

//* Chats
const Chats = Loadable(lazy(() => import('views/chats')));
//* Constant use
const FavouriteMessages = Loadable(lazy(() => import('views/favourite-messages')));
//* Contacts
const Contacts = Loadable(lazy(() => import('views/contacts')));
const ContactsChat = Loadable(lazy(() => import('views/contacts/chat')));
const SingleChatUserPage = Loadable(lazy(() => import('views/contacts/chat/single-user')));
const ImportChatUsersFromFile = Loadable(lazy(() => import('views/contacts/chat/import-wizard')));
const ContactsCustomers = Loadable(lazy(() => import('views/contacts/customers')));
const ContactsPriority = Loadable(lazy(() => import('views/contacts/priority-contacts')));
const ContactsPriorityCustomers = Loadable(lazy(() => import('views/contacts/priority-customers')));
const SyncPriorityContacts = Loadable(lazy(() => import('views/contacts/chat/priority-sync')));

//* User config
const UserConfigurations = Loadable(lazy(() => import('views/configurations')));
const UserChatConfigurations = Loadable(lazy(() => import('views/configurations/chat')));
const UserSystemConfigurations = Loadable(lazy(() => import('views/configurations/system')));
const UserExtensionConfigurations = Loadable(lazy(() => import('views/configurations/extension')));
// const Profile = Loadable(lazy(() => import('views/profile/index_old')));
const UserProfile = Loadable(lazy(() => import('views/profile')));

//** Templates
const MessagesTemplates = Loadable(lazy(() => import('views/messages-templates')));
const TemplatesList = Loadable(lazy(() => import('views/messages-templates/list')));
const DraftMessagesTemplates = Loadable(lazy(() => import('views/messages-templates/drafts')));
const PredefinedMessagesTemplates = Loadable(lazy(() => import('views/messages-templates/predefined')));
const TemplatesDefaultSettings = Loadable(lazy(() => import('views/messages-templates/default-settings')));
//** System management
//* Company info
const CompanyIndex = Loadable(lazy(() => import('views/company')));
const CompanyDetails = Loadable(lazy(() => import('views/company/details')));
const CompanyConversationsSettings = Loadable(lazy(() => import('views/company/conversations')));
const CompanyFacebookNotifications = Loadable(lazy(() => import('views/company/facebook')));
const CompanyAbout = Loadable(lazy(() => import('views/company/about')));
const CompanyExtensions = Loadable(lazy(() => import('views/company/extensions')));
const CompanyChatCustomization = Loadable(lazy(() => import('views/company/chat-customization')));
// const CompanyDocsSettings = Loadable(lazy(() => import('views/company/docs')));
//* App users
const ApplicationUsers = Loadable(lazy(() => import('views/application-users')));
const ApplicationUser = Loadable(lazy(() => import('views/application-users/user')));
const CreateApplicationUser = Loadable(lazy(() => import('views/application-users/create')));

const Labels = Loadable(lazy(() => import('views/labels')));
const Files = Loadable(lazy(() => import('views/files')));
const SimpleTemplatesList = Loadable(lazy(() => import('views/files/simple-templates')));
//* Advanced use
//* Mass messaging groups
const MassMessagingGroups = Loadable(lazy(() => import('views/mass-messaging/groups')));
const MassMessagingGroupCreateEdit = Loadable(
  lazy(() => import('views/mass-messaging/groups/create-edit-group')),
);
//* Mass messaging tasks
const MassMessagingTasks = Loadable(lazy(() => import('views/mass-messaging/tasks')));
const MassMessagingTasksList = Loadable(lazy(() => import('views/mass-messaging/tasks/TasksList')));
const CreateEditMassMessagingTask = Loadable(
  lazy(() => import('views/mass-messaging/tasks/create-edit-task')),
);
const MassMessagingTaskLogs = Loadable(lazy(() => import('views/mass-messaging/tasks/logs')));
//* Mass messaging mailing
const WhatsappMailing = Loadable(lazy(() => import('views/mass-messaging/mail')));
//TODO Workflows
const ManageBotsWrapper = Loadable(lazy(() => import('views/bots/ManageBotsWrapper')));
//* Digital Signature
const DocsSign = Loadable(lazy(() => import('views/docs-sign')));
//* AI Features
const AIAnalyze = Loadable(lazy(() => import('views/ai/ai-analyze')));
const AISupport = Loadable(lazy(() => import('views/ai/ai-support')));
const PromptsTab = Loadable(lazy(() => import('views/ai/ai-support/prompts')));
const ConnectionsTab = Loadable(lazy(() => import('views/ai/ai-support/connections')));
//* Integrations
const IntegrationsPage = Loadable(lazy(() => import('views/integrations')));
const WebhooksPage = Loadable(lazy(() => import('views/integrations/webhooks')));
const IntegrationsTabPage = Loadable(lazy(() => import('views/integrations/integrations')));
//* Fireberry
const FireberrySettingsPage = Loadable(lazy(() => import('views/fireberry')));
const FireberryCompany = Loadable(lazy(() => import('views/fireberry/company')));
const FireberryClientsAndContacts = Loadable(lazy(() => import('views/fireberry/clients')));
const FireberryObjectsPage = Loadable(lazy(() => import('views/fireberry/objects')));
const FireberrySync = Loadable(lazy(() => import('views/fireberry/sync')));
//* priority
const PrioritySettingsPage = Loadable(lazy(() => import('views/priority/priority-settings-page')));
const PriorityAppLinks = Loadable(lazy(() => import('views/priority/priority-settings-page/app-links')));
const PriorityCredentials = Loadable(lazy(() => import('views/priority/priority-settings-page/credentials')));
const PriorityConfigurations = Loadable(
  lazy(() => import('views/priority/priority-settings-page/configurations')),
);
const PriorityFields = Loadable(lazy(() => import('views/priority/priority-settings-page/fields')));
const PriorityMassActions = Loadable(
  lazy(() => import('views/priority/priority-settings-page/mass-actions')),
);
const ProceduresAndFormats = Loadable(
  lazy(() => import('views/priority/priority-settings-page/procedures-and-formats')),
);
const PrCompanies = Loadable(lazy(() => import('views/priority/priority-settings-page/companies')));
const PrCompanyProcedures = Loadable(
  lazy(() => import('views/priority/priority-settings-page/company-procedures')),
);
const PrConnections = Loadable(lazy(() => import('views/priority/priority-settings-page/connections')));

//TODO
const AppKanban = Loadable(lazy(() => import('views/kanban')));
const AppKanbanBacklogs = Loadable(lazy(() => import('views/kanban/Backlogs')));
const AppKanbanBoard = Loadable(lazy(() => import('views/kanban/Board')));
const AppCalendar = Loadable(lazy(() => import('views/calendar')));

//* Links Tree (used for mapping links inside the blocks)
const LinksTree = Loadable(lazy(() => import('views/links-tree')));

//* Ops console (admin company only)
const OpsConsole = Loadable(lazy(() => import('views/ops-console')));
// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes: RouteObject = {
  path: '/',
  element: (
    <AuthGuard>
      <MainLayout />
    </AuthGuard>
  ),
  errorElement: <ErrorBoundary />,
  children: [
    //* Chats
    {
      path: '/chats',
      element: <Chats />,
      children: [{ path: '/chats/:chatUserId', element: <Chats /> }],
    },
    {
      path: '/chats/my',
      element: <Chats />,
      children: [{ path: '/chats/my/:chatUserId', element: <Chats /> }],
    },
    {
      path: '/chats/not-my',
      element: <Chats />,
      children: [{ path: '/chats/not-my/:chatUserId', element: <Chats /> }],
    },
    {
      path: '/chats/unassigned',
      element: <Chats />,
      children: [{ path: '/chats/unassigned/:chatUserId', element: <Chats /> }],
    },
    {
      path: '/chats/bots',
      element: <Chats />,
      children: [{ path: '/chats/bots/:chatUserId', element: <Chats /> }],
    },
    {
      path: '/chats/custom/:viewId',
      element: <Chats />,
      children: [{ path: '/chats/custom/:viewId/:chatUserId', element: <Chats /> }],
    },
    //* Constant use
    {
      path: '/constant-use',
      element: <LinksTree />,
    },
    {
      path: '/favourite-messages',
      element: <FavouriteMessages />,
    },
    {
      path: '/contacts',
      element: <Contacts />,
      children: [
        {
          path: 'chat',
          element: <ContactsChat />,
        },
        {
          path: 'customers',
          element: <ContactsCustomers />,
        },
        {
          path: 'import',
          element: <ImportChatUsersFromFile />,
        },
        {
          path: 'priority-contacts',
          element: <ContactsPriority />,
        },
        {
          path: 'priority-customers',
          element: <ContactsPriorityCustomers />,
        },
        {
          path: 'priority-sync',
          element: <SyncPriorityContacts />,
        },
      ],
    },
    { path: '/contacts/chat/:chatUserId', element: <SingleChatUserPage /> },
    //** Templates
    {
      path: '/templates',
      element: <MessagesTemplates />,
      children: [
        {
          index: true,
          element: <Navigate to="/templates/list" replace />,
        },
        {
          path: 'list',
          element: <TemplatesList key="all-templates" />,
        },
        {
          path: 'drafts',
          element: <DraftMessagesTemplates />,
        },
        {
          path: 'rejected',
          element: <TemplatesList key="all-templates" />,
        },
        {
          path: 'default-settings',
          element: <TemplatesDefaultSettings />,
        },
        {
          path: 'predefined',
          element: <PredefinedMessagesTemplates />,
        },
        {
          path: 'simple',
          element: <SimpleTemplatesList />,
        },
      ],
    },
    { path: '/templates/create', element: <CreateEditTemplate /> },
    { path: '/templates/edit/:id', element: <CreateEditTemplate /> },
    {
      path: '/templates/drafts/:id',
      element: <CreateEditTemplate templateType="draft" />,
    },
    {
      path: '/customization',
      element: <UserConfigurations />,
      children: [
        {
          path: 'profile',
          element: <UserProfile />,
        },
        {
          path: 'chat',
          element: <UserChatConfigurations />,
        },
        {
          path: 'style',
          element: <UserSystemConfigurations />,
        },
        {
          path: 'extension',
          element: <UserExtensionConfigurations />,
        },
      ],
    },
    {
      path: '/profile',
      element: <UserProfile />,
    },
    //* System management
    {
      path: '/system-management',
      element: <LinksTree />,
    },
    {
      path: '/company',
      element: <CompanyIndex />,
      children: [
        { path: 'details', element: <CompanyDetails /> },
        { path: 'conversation', element: <CompanyConversationsSettings /> },
        {
          path: 'facebook-notifications',
          element: <CompanyFacebookNotifications />,
        },
        { path: 'about', element: <CompanyAbout /> },
        { path: 'extensions', element: <CompanyExtensions /> },
        { path: 'chat-customization', element: <CompanyChatCustomization /> },
        // { path: 'documents', element: <CompanyDocsSettings /> },
      ],
    },
    {
      path: '/users',
      element: <ApplicationUsers />,
    },
    {
      path: '/users/:id',
      element: <ApplicationUser />,
    },
    {
      path: '/users/create',
      element: <CreateApplicationUser />,
    },
    {
      path: '/labels',
      element: <Labels />,
    },
    {
      path: '/drive',
      element: <Files />,
      children: [
        {
          path: 'images',
          element: <FilesListLayout type="image" />,
        },
        {
          path: 'videos',
          element: <FilesListLayout type="video" />,
        },
        {
          path: 'documents',
          element: <FilesListLayout type="document" />,
        },
        {
          path: 'templates',
          element: <Navigate to="/templates/simple" replace />,
        },
      ],
    },
    //* Advanced use
    {
      path: '/advanced-use',
      element: <LinksTree />,
    },
    {
      path: '/broadcasts-lists',
      element: <MassMessagingGroups />,
    },
    {
      path: '/broadcasts-lists/create',
      element: <MassMessagingGroupCreateEdit />,
    },
    {
      path: '/broadcasts-lists/edit/:id',
      element: <MassMessagingGroupCreateEdit />,
    },
    {
      path: '/broadcasts',
      element: <MassMessagingTasks />,
      children: [
        {
          path: '/broadcasts/lists',
          element: <MassMessagingGroups />,
        },
        {
          path: 'planned',
          element: <MassMessagingTasksList listType="planned" key="planned" />,
        },
        {
          path: 'running',
          element: <MassMessagingTasksList listType="running" key="running" />,
        },
        {
          path: 'finished',
          element: <MassMessagingTasksList listType="ended" key="ended" />,
        },
      ],
    },
    {
      path: '/broadcasts/create',
      element: <CreateEditMassMessagingTask />,
    },
    {
      path: '/broadcasts/edit/:id',
      element: <CreateEditMassMessagingTask />,
    },
    {
      path: '/broadcast-logs/:id',
      element: <MassMessagingTaskLogs />,
    },
    {
      path: '/mail-whatsapp',
      element: <WhatsappMailing />,
    },
    {
      path: '/workflows',
      element: <ManageBotsWrapper />,
    },
    //* AI Features
    {
      path: '/ai',
      element: <LinksTree />,
    },
    {
      path: '/ai-analyze',
      element: <AIAnalyze />,
    },
    {
      path: '/ai-support',
      element: <AISupport />,
      children: [
        {
          path: 'prompts',
          element: <PromptsTab />,
        },
        {
          path: 'connections',
          element: <ConnectionsTab />,
        },
      ],
    },
    //* Integrations
    {
      path: '/webhooks',
      element: <IntegrationsPage />,
      children: [
        {
          path: 'webhooks',
          element: <WebhooksPage />,
        },
        {
          path: 'integrations',
          element: <IntegrationsTabPage />,
        },
      ],
    },
    {
      path: '/fireberry',
      element: <FireberrySettingsPage />,
      children: [
        {
          path: 'company',
          element: <FireberryCompany />,
        },
        {
          path: 'clients-and-contacts',
          element: <FireberryClientsAndContacts />,
        },
        {
          path: 'objects',
          element: <FireberryObjectsPage />,
        },
        {
          path: 'sync',
          element: <FireberrySync />,
        },
      ],
    },
    {
      path: '/priority',
      element: (
        <PriorityGuard>
          <PrioritySettingsPage />
        </PriorityGuard>
      ),
      children: [
        {
          index: true,
          element: <Navigate to="app-links" replace />,
        },
        {
          path: 'app-links',
          element: <PriorityAppLinks />,
        },
        {
          path: 'credentials',
          element: <PriorityCredentials />,
        },
        {
          path: 'configurations',
          element: <PriorityConfigurations />,
        },
        {
          path: 'fields',
          element: <PriorityFields />,
        },
        {
          path: 'mass-actions',
          element: <PriorityMassActions />,
        },
        {
          path: 'companies',
          element: <PrCompanies />,
        },
        {
          path: 'company-procedures',
          element: <PrCompanyProcedures />,
        },
        {
          path: 'procedures',
          element: <ProceduresAndFormats />,
        },
        {
          path: 'connections',
          element: <PrConnections />,
        },
      ],
    },
    {
      path: '/kanban',
      element: <AppKanban />,
      // element: <ComingSoon />,
      children: [
        {
          path: 'backlogs',
          element: <AppKanbanBacklogs />,
          // element: <ComingSoon />,
        },
        {
          path: 'board',
          element: <AppKanbanBoard />,
          // element: <ComingSoon />,
        },
      ],
    },
    {
      path: '/calendar',
      element: <AppCalendar />,
      // element: <ComingSoon />,
    },
    {
      path: '/docs-sign',
      element: <DocsSign />,
    },
    {
      path: '/external-app/:appId',
      element: <ExternalAppPage />,
    },
    {
      path: '/mail-whatsapp',
      element: <ComingSoon />,
    },
    {
      path: '/data-export',
      element: <ComingSoon />,
    },
    {
      path: '/statistics',
      element: <Statistics />,
    },
    {
      path: '/ops-console',
      element: <OpsConsole />,
    },
  ],
};

export default MainRoutes;
